import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { User } from '@domain/models';
import { AuthInteractionInProgressError, AuthProviderPort, UserRepositoryPort } from '@domain/ports';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private userRepo = inject(UserRepositoryPort);
  private authProvider = inject(AuthProviderPort, { optional: true });

  private _currentUser = signal<User | null>(null);
  private _ready = signal<boolean>(false);
  private _initializing = signal<boolean>(false);
  private _authError = signal<string | null>(null);

  private initPromise: Promise<void> | null = null;

  readonly currentUser = this._currentUser.asReadonly();
  readonly ready = this._ready.asReadonly();
  readonly initializing = this._initializing.asReadonly();
  readonly authError = this._authError.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  hasMsalConfig(): boolean {
    return this.authProvider?.isConfigured() ?? false;
  }

  async ensureSession(): Promise<void> {
    return this.resolveSession();
  }

  async completeLoginRedirect(): Promise<void> {
    return this.resolveSession();
  }

  private async resolveSession(): Promise<void> {
    if (this.isAuthenticated()) {
      this._ready.set(true);
      return;
    }

    if (this.initPromise) return this.initPromise;

    this.initPromise = this.initializeInternal().finally(() => {
      this._ready.set(true);
    });

    return this.initPromise;
  }

  async login(): Promise<boolean> {
    await this.ensureSession();

    if (this.isAuthenticated()) return true;
    if (!this.hasMsalConfig()) {
      this._authError.set(
        'El inicio de sesión no está disponible por el momento. Inténtalo más tarde.'
      );
      return false;
    }

    this._authError.set(null);
    try {
      await this.authProvider?.login();
      return true;
    } catch (error) {
      if (error instanceof AuthInteractionInProgressError) {
        this._authError.set(
          'Ya hay un inicio de sesión en curso. Espera unos segundos o recarga la página para intentarlo nuevamente.'
        );
        return false;
      }

      throw error;
    }
  }

  async logout(): Promise<boolean> {
    this._currentUser.set(null);
    this._authError.set(null);

    return this.authProvider?.logout() ?? false;
  }

  private async initializeInternal(): Promise<void> {
    if (!this.hasMsalConfig()) {
      this._currentUser.set(null);
      return;
    }

    this._initializing.set(true);
    this._authError.set(null);

    try {
      const hasProviderSession = await this.authProvider?.initialize() ?? false;

      if (!hasProviderSession) {
        this._currentUser.set(null);
        return;
      }

      const user = await this.resolveAppUser();
      this._currentUser.set(user);

      if (!user) {
        this._authError.set(
          'No pudimos cargar tu perfil. Inténtalo nuevamente.'
        );
      }
    } catch (error) {
      console.error('MSAL initialization failed', error);
      this._currentUser.set(null);
      this._authError.set('No pudimos iniciar sesión. Inténtalo nuevamente.');
    } finally {
      this._initializing.set(false);
    }
  }

  private async resolveAppUser(): Promise<User | null> {
    try {
      return await firstValueFrom(this.userRepo.getCurrent());
    } catch (error) {
      console.warn('Unable to resolve /me user profile', error);
      return null;
    }
  }
}
